<template>
  <div class="image-crop-upload">
    <!-- 上传卡片 -->
    <div class="cover-card" v-if="!innerUrl" @click="triggerPick">
      <i class="el-icon-plus"></i>
      <div class="card-tip">点击上传封面</div>
    </div>
    <div class="cover-card has-img" v-else>
      <img :src="resolvedUrl" alt="封面预览" />
      <div class="cover-mask">
        <span class="mask-btn" @click="triggerPick">
          <i class="el-icon-refresh-right"></i> 替换
        </span>
        <span class="mask-btn danger" @click="handleRemove">
          <i class="el-icon-delete"></i> 删除
        </span>
      </div>
    </div>

    <!-- 友好提示 -->
    <div class="hint">
      建议尺寸 <b>{{ outputWidth }} × {{ outputHeight }}</b>（{{ ratioLabel }}），
      支持 jpg / png / webp，单张 ≤ {{ maxSizeMB }}MB，上传后可裁剪。
    </div>

    <!-- 隐藏的文件选择 -->
    <input
      ref="fileInput"
      type="file"
      accept="image/jpeg,image/png,image/webp"
      style="display: none;"
      @change="handlePick"
    />

    <!-- 裁剪弹窗 -->
    <el-dialog
      title="裁剪封面"
      v-model="cropDialogVisible"
      width="780px"
      :close-on-click-modal="false"
      append-to-body
      destroy-on-close
    >
      <div class="crop-tip">拖动 / 缩放裁剪框，右侧为最终展示效果。</div>
      <div class="crop-body">
        <div class="cropper-area">
          <vue-cropper
            v-if="cropDialogVisible"
            ref="cropperRef"
            :img="srcImg"
            :auto-crop="true"
            :auto-crop-width="autoCropWidth"
            :auto-crop-height="autoCropHeight"
            :fixed="true"
            :fixed-number="fixedRatio"
            :center-box="true"
            :info="true"
            :output-type="'jpeg'"
            :output-size="0.92"
            :can-move-box="true"
            :can-scale="true"
            :original="false"
          />
        </div>
        <div class="preview-area">
          <div class="preview-title">预览</div>
          <div class="preview-box" :style="previewBoxStyle">
            <img v-if="previewUrl" :src="previewUrl" :style="previewImgStyle" />
          </div>
          <div class="preview-mini-title">缩略图</div>
          <div class="preview-mini" :style="previewMiniStyle">
            <img v-if="previewUrl" :src="previewUrl" :style="previewMiniImgStyle" />
          </div>
        </div>
      </div>
      <template #footer>
        <el-button @click="cropDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="uploading" @click="handleConfirmCrop">
          确认上传
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { VueCropper } from 'vue-cropper'
import 'vue-cropper/dist/index.css'
import globle from '../../globle/globle'

export default {
  name: 'ImageCropUpload',
  components: { VueCropper },
  props: {
    modelValue: { type: String, default: '' },
    ratio: { type: Array, default: () => [1, 1] },
    outputWidth: { type: Number, default: 800 },
    outputHeight: { type: Number, default: 800 },
    maxSizeMB: { type: Number, default: 2 },
    minWidth: { type: Number, default: 600 },
    minHeight: { type: Number, default: 600 },
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      GLOBAL: globle,
      cropDialogVisible: false,
      srcImg: '',
      previewUrl: '',
      previewTimer: null,
      uploading: false,
    }
  },
  computed: {
    innerUrl() {
      return this.modelValue || ''
    },
    resolvedUrl() {
      const u = this.innerUrl
      if (!u) return ''
      if (/^https?:\/\//i.test(u) || u.startsWith('data:')) return u
      const prefix = (this.GLOBAL && this.GLOBAL.imgPath) || ''
      return prefix + u
    },
    fixedRatio() {
      return this.ratio
    },
    ratioLabel() {
      const [w, h] = this.ratio
      return w === h ? `${w}:${h} 正方形` : `${w}:${h}`
    },
    autoCropWidth() {
      return Math.min(this.outputWidth, 360)
    },
    autoCropHeight() {
      return Math.min(this.outputHeight, 360)
    },
    previewBoxStyle() {
      const [w, h] = this.ratio
      const base = 200
      return {
        width: base + 'px',
        height: (base * h) / w + 'px',
      }
    },
    previewImgStyle() {
      return { width: '100%', height: '100%', objectFit: 'cover' }
    },
    previewMiniStyle() {
      const [w, h] = this.ratio
      const base = 60
      return {
        width: base + 'px',
        height: (base * h) / w + 'px',
      }
    },
    previewMiniImgStyle() {
      return { width: '100%', height: '100%', objectFit: 'cover' }
    },
  },
  methods: {
    triggerPick() {
      this.$refs.fileInput.value = ''
      this.$refs.fileInput.click()
    },
    handleRemove() {
      this.$emit('update:modelValue', '')
      this.$emit('change', '')
    },
    handlePick(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      const okType = /image\/(jpeg|png|webp)/.test(file.type)
      if (!okType) {
        this.$message.error('仅支持 jpg / png / webp 格式')
        return
      }
      const okSize = file.size <= this.maxSizeMB * 1024 * 1024
      if (!okSize) {
        this.$message.error(`图片大小不能超过 ${this.maxSizeMB}MB`)
        return
      }
      const reader = new FileReader()
      reader.onload = (ev) => {
        const dataUrl = ev.target.result
        const img = new Image()
        img.onload = () => {
          if (img.width < this.minWidth || img.height < this.minHeight) {
            this.$message.error(
              `图片尺寸至少 ${this.minWidth}×${this.minHeight}，当前 ${img.width}×${img.height}`
            )
            return
          }
          this.srcImg = dataUrl
          this.cropDialogVisible = true
          this.$nextTick(() => this.startPreviewLoop())
        }
        img.onerror = () => this.$message.error('图片解析失败')
        img.src = dataUrl
      }
      reader.readAsDataURL(file)
    },
    startPreviewLoop() {
      this.stopPreviewLoop()
      this.previewTimer = setInterval(() => {
        const cropper = this.$refs.cropperRef
        if (!cropper || !cropper.getCropData) return
        cropper.getCropData((data) => {
          this.previewUrl = data
        })
      }, 300)
    },
    stopPreviewLoop() {
      if (this.previewTimer) {
        clearInterval(this.previewTimer)
        this.previewTimer = null
      }
    },
    async handleConfirmCrop() {
      const cropper = this.$refs.cropperRef
      if (!cropper) return
      this.uploading = true
      cropper.getCropBlob(async (blob) => {
        try {
          const resized = await this.resizeBlob(blob, this.outputWidth, this.outputHeight)
          const fd = new FormData()
          fd.append('file', resized, `cover_${Date.now()}.jpg`)
          const res = await this.$multipartPost('/business/base/image/upload', fd)
          if (res.data && res.data.code === 0) {
            const url = res.data.msg || res.data.data || ''
            if (!url) {
              this.$message.error('上传成功但未返回图片路径')
              this.uploading = false
              return
            }
            this.$emit('update:modelValue', url)
            this.$emit('change', url)
            this.$message.success('封面上传成功')
            this.cropDialogVisible = false
          } else {
            this.$message.error((res.data && res.data.msg) || '上传失败')
          }
        } catch (err) {
          console.error('封面上传失败', err)
          this.$message.error('上传失败')
        } finally {
          this.uploading = false
        }
      })
    },
    resizeBlob(blob, targetW, targetH) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement('canvas')
          canvas.width = targetW
          canvas.height = targetH
          const ctx = canvas.getContext('2d')
          ctx.fillStyle = '#fff'
          ctx.fillRect(0, 0, targetW, targetH)
          ctx.drawImage(img, 0, 0, targetW, targetH)
          canvas.toBlob(
            (out) => (out ? resolve(out) : reject(new Error('canvas toBlob failed'))),
            'image/jpeg',
            0.9
          )
        }
        img.onerror = reject
        img.src = URL.createObjectURL(blob)
      })
    },
  },
  watch: {
    cropDialogVisible(v) {
      if (!v) {
        this.stopPreviewLoop()
        this.previewUrl = ''
      }
    },
  },
  beforeUnmount() {
    this.stopPreviewLoop()
  },
}
</script>

<style scoped lang="less">
.image-crop-upload {
  display: inline-block;
}
.cover-card {
  width: 158px;
  height: 158px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  background: #fafafa;
  color: #8c939d;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: border-color 0.2s;

  &:hover {
    border-color: #679ff6;
    color: #679ff6;
  }

  i {
    font-size: 28px;
  }

  .card-tip {
    margin-top: 6px;
    font-size: 13px;
  }

  &.has-img {
    border-style: solid;
    border-color: #ebeef5;
    background: #fff;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .cover-mask {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.55);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      opacity: 0;
      transition: opacity 0.2s;
      font-size: 13px;
    }

    &:hover .cover-mask {
      opacity: 1;
    }

    .mask-btn {
      cursor: pointer;
      padding: 4px 8px;
      border-radius: 3px;

      &:hover {
        background: rgba(255, 255, 255, 0.15);
      }

      &.danger:hover {
        background: rgba(245, 108, 108, 0.4);
      }
    }
  }
}
.hint {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;

  b {
    color: #606266;
  }
}
.crop-tip {
  font-size: 12px;
  color: #909399;
  margin-bottom: 12px;
}
.crop-body {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}
.cropper-area {
  flex: 1;
  height: 400px;
  background: #2a2a2a;
  border-radius: 4px;
  overflow: hidden;
}
.preview-area {
  width: 220px;
  flex-shrink: 0;
}
.preview-title,
.preview-mini-title {
  font-size: 12px;
  color: #606266;
  margin-bottom: 8px;
}
.preview-mini-title {
  margin-top: 16px;
}
.preview-box,
.preview-mini {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
  background: #fafafa;
}
</style>
